import { normalizeLegacyPlatformDomain, normalizeLegacyPlatformUrl } from '@/lib/platformDomain';

type SiteLike = {
  id?: number | string | null;
  domain?: string | null;
  originalDomain?: string | null;
  siteKey?: string | null;
  status?: number | null;
  sslCertStatus?: string | null;
};

type SitePathLike = {
  siteId?: number | string | null;
  domain?: string | null;
  originalDomain?: string | null;
  path?: string | null;
};

const normalizePublishedPath = (path?: string | null) =>
  String(path || '')
    .trim()
    .replace(/^\/+/, '')
    .replace(/\/+$/, '');

export const isSiteDnsVerified = (site?: Pick<SiteLike, 'status' | 'sslCertStatus'> | null) =>
  Boolean(site && (site.status === 2 || site.sslCertStatus === 'ACTIVE'));

export const getPublishedPageDomain = ({
  site,
  sitePath,
  includeSiteKeyFallback = true,
}: {
  site?: SiteLike | null;
  sitePath?: SitePathLike | null;
  includeSiteKeyFallback?: boolean;
} = {}) => {
  const verifiedCustomDomain = isSiteDnsVerified(site)
    ? normalizeLegacyPlatformDomain(site?.domain || sitePath?.domain)
    : '';
  if (verifiedCustomDomain) return verifiedCustomDomain;

  const hasSiteVerificationState = site?.status != null || Boolean(site?.sslCertStatus);
  if (!hasSiteVerificationState) {
    const boundDomain = normalizeLegacyPlatformDomain(sitePath?.domain);
    if (boundDomain) return boundDomain;
  }

  const platformDomain = normalizeLegacyPlatformDomain(
    site?.originalDomain || sitePath?.originalDomain,
  );
  if (platformDomain) return platformDomain;

  if (includeSiteKeyFallback) {
    const normalizedSiteKey = normalizeLegacyPlatformDomain(site?.siteKey);
    if (normalizedSiteKey) return normalizedSiteKey;
  }

  return '';
};

export const buildPublishedPageUrl = ({
  site,
  sitePath,
  path,
  fallbackUrl,
}: {
  site?: SiteLike | null;
  sitePath?: SitePathLike | null;
  path?: string | null;
  fallbackUrl?: string | null;
} = {}) => {
  const publishedDomain = getPublishedPageDomain({ site, sitePath });
  const publishedPath = normalizePublishedPath(path ?? sitePath?.path);

  if (publishedDomain) {
    return publishedPath
      ? `https://${publishedDomain}/${publishedPath}`
      : `https://${publishedDomain}`;
  }

  return normalizeLegacyPlatformUrl(fallbackUrl);
};
